import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Product } from "../../types/product";
import { useCart } from "../../context/CartContext";

const Card = styled.article`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radii.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
  transition: all 0.25s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.colors.borderHover};
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 220px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
`;

const ProductImage = styled.img`
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
`;

const Content = styled.div`
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const Category = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
  padding: 0.25rem 0.6rem;
  border-radius: ${({ theme }) => theme.radii.full};
  align-self: flex-start;
`;

const Title = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Price = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-top: auto;
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Star = styled.span`
  color: ${({ theme }) => theme.colors.warning};
`;

const ButtonRow = styled.div`
  display: flex;
`;

const ViewButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 0 0 0 ${({ theme }) => theme.radii.lg};
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
  }
`;

const AddToCartButton = styled.button<{ $inCart?: boolean }>`
  flex: 1;
  padding: 0.75rem;
  background: ${({ $inCart, theme }) =>
    $inCart ? theme.colors.successLight : theme.colors.secondary};
  color: ${({ $inCart, theme }) =>
    $inCart ? theme.colors.successText : "white"};
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 0 0 ${({ theme }) => theme.radii.lg} 0;
  transition: background 0.2s;
  border-left: 1px solid rgba(255, 255, 255, 0.15);

  &:hover {
    background: ${({ $inCart, theme }) =>
      $inCart ? theme.colors.successLight : theme.colors.secondaryHover};
  }
`;

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  const inCart = isInCart(product.id);

  return (
    <Card>
      <ImageWrapper>
        <ProductImage src={product.image} alt={product.title} />
      </ImageWrapper>
      <Content>
        <Category>{product.category}</Category>
        <Title>{product.title}</Title>
        <Rating>
          <Star>★</Star>
          {product.rating.rate} ({product.rating.count})
        </Rating>
        <Price>${product.price.toFixed(2)}</Price>
      </Content>
      <ButtonRow>
        <ViewButton onClick={() => navigate(`/products/${product.id}`)}>
          View Details
        </ViewButton>
        <AddToCartButton $inCart={inCart} onClick={() => addToCart(product)}>
          {inCart ? "✓ In Cart" : "+ Add"}
        </AddToCartButton>
      </ButtonRow>
    </Card>
  );
};

export default ProductCard;
